<template>
  <div class="item-row-wrapper">
    <!-- Tooltip showing full path on hover (only when scrollablePath is present) -->
    <q-tooltip
      v-if="config.secondary?.scrollablePath"
      class="tw-bg-gray-dark tw-text-white"
      anchor="top middle"
      self="bottom middle"
      :delay="300"
    >
      {{ getFullPathString() }}
    </q-tooltip>

    <!-- Two-row layout for items with scrollable breadcrumb path -->
    <div
      v-if="config.secondary?.scrollablePath?.items?.length"
      class="item-row item-row--two-row"
      :class="computedRowClasses"
      v-bind="$attrs"
      :data-navigable="true"
      :tabindex="focused ? 0 : -1"
      @click="handleRowClick"
      @keydown.enter.prevent="handleRowClick"
      @keydown.space.prevent="handleRowClick"
    >
      <!-- Row 1: Checkbox, Icon, Title -->
      <div class="tw-flex tw-items-center tw-gap-2">
        <!-- Checkbox for multi-select -->
        <q-checkbox
          v-if="showCheckbox && !config.hideCheckbox"
          :model-value="selected"
          size="xs"
          dense
          class="tw-pointer-events-none tw-shrink-0"
          tabindex="-1"
        />

        <!-- Icon -->
        <item-row-icon :config="config.icon" />

        <!-- Primary text -->
        <span
          v-if="config.primary"
          class="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium"
          :style="getPrimaryStyle"
          :class="config.primary.className"
        >
          {{ config.primary.text }}
        </span>

        <!-- Badge -->
        <span
          v-if="config.badge && !config.badge.multiple"
          class="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium"
          :class="getBadgeClasses(config.badge)"
          :style="getBadgeStyles(config.badge)"
        >
          {{ config.badge.text }}
        </span>

        <!-- Navigation chevron -->
        <octicon
          v-if="config.showNavigationArrow"
          class="navigation-chevron tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded"
          icon="chevron-right"
          :height="16"
          @click.stop="handleNavigateClick"
        />
      </div>

      <!-- Row 2: Breadcrumb (left justified, aligned with title after checkbox + icon) -->
      <div
        ref="scrollablePathRef"
        class="scrollable-path tw-mt-1"
        :class="
          showCheckbox && !config.hideCheckbox
            ? 'scrollable-path--with-checkbox'
            : 'scrollable-path--no-checkbox'
        "
      >
        <div class="scrollable-path__content">
          <template
            v-for="(pathItem, index) in config.secondary.scrollablePath.items"
            :key="index"
          >
            <span
              v-if="index > 0"
              class="scrollable-path__separator tw-text-gray-medium"
            >
              {{ config.secondary.scrollablePath.separator || '›' }}
            </span>
            <span
              class="scrollable-path__item tw-italic tw-text-gray-medium-dark"
            >
              {{ pathItem }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <!-- Standard layout using BaseRow (no scrollable path) -->
    <base-row
      v-else
      v-bind="$attrs"
      :selected="selected"
      :focused="focused"
      :disabled="disabled"
      :archived="archived"
      :hierarchical="config.hierarchical"
      :show-checkbox="showCheckbox && !config.hideCheckbox"
      :item="item"
      :custom-class="customClass"
      @click="handleClick"
      @item-selected="handleItemSelected"
      @item-toggled="handleItemToggled"
    >
      <!-- Icon/Avatar slot -->
      <template v-if="config.icon" #icon>
        <item-row-icon :config="config.icon" />
      </template>

      <!-- Primary text -->
      <template #primary>
        <span
          v-if="config.primary"
          :style="getPrimaryStyle"
          :class="config.primary.className"
        >
          {{ config.primary.text }}
        </span>
      </template>

      <!-- Secondary text - non-scrollable variants only -->
      <template v-if="config.secondary" #secondary>
        <!-- Multi-part secondary text -->
        <div
          v-if="config.secondary.parts && config.secondary.parts.length > 0"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span v-for="(part, index) in config.secondary.parts" :key="index">
            <span v-if="index > 0 && part.separator">{{ part.separator }}</span>
            <span :class="part.className">{{ part.text }}</span>
          </span>
        </div>
        <!-- Simple text -->
        <span
          v-else-if="config.secondary.text"
          :class="config.secondary.className"
        >
          {{ config.secondary.text }}
        </span>
      </template>

      <!-- Badge slot -->
      <template v-if="config.badge" #badge>
        <div
          v-if="config.badge.multiple"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <q-badge
            v-for="(badge, index) in config.badge.items"
            :key="index"
            :color="badge.color || 'grey-5'"
            :text-color="badge.textColor || 'dark'"
            :outline="badge.outline"
          >
            {{ badge.text }}
          </q-badge>
        </div>

        <span
          v-else
          class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium"
          :class="getBadgeClasses(config.badge)"
          :style="getBadgeStyles(config.badge)"
        >
          {{ config.badge.text }}
        </span>
      </template>

      <!-- Action slot for navigation chevron -->
      <template v-if="config.showNavigationArrow" #action>
        <octicon
          class="navigation-chevron tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded"
          icon="chevron-right"
          :height="16"
          @click.stop="handleNavigateClick"
        />
      </template>
    </base-row>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  nextTick,
  onMounted,
  h,
  type FunctionalComponent,
} from 'vue';
import { QCheckbox, QIcon, QTooltip } from 'quasar';
import { UserAvatar } from '@/components/avatars';
import CustomIcon, {
  type CustomIconName,
} from '@/components/core/icons/CustomIcon.vue';
import FluentIcon, {
  type FluentIconName,
} from '@/components/core/icons/FluentIcon.vue';
import Octicon from '@/components/core/icons/Octicon.vue';
import BaseRow from '../rows/BaseRow.vue';
import type { OrganizationMembership } from '@/models/user.models';

// Functional component for rendering icons based on config
const ItemRowIcon: FunctionalComponent<{ config?: ItemRowConfig['icon'] }> = (
  props
) => {
  if (!props.config) return null;

  const { type, user, hideInactiveWarning, name, color, size, className } =
    props.config;

  if (type === 'avatar' && user) {
    return h(UserAvatar, {
      member: user,
      size: size || '32px',
      hideInactiveWarning,
      class: 'tw-shrink-0',
    });
  }

  if (type === 'dot') {
    return h('div', {
      class: ['tw-shrink-0', 'tw-rounded-full', className || 'tw-w-3 tw-h-3'],
      style: { backgroundColor: color },
    });
  }

  if (type === 'icon' && name) {
    return h(QIcon, {
      name,
      size: size || '16px',
      style: { color },
      class: className,
    });
  }

  if (type === 'fluent' && name) {
    return h(FluentIcon, {
      icon: name as FluentIconName,
      size: size || '16px',
      style: { color },
      class: className,
    });
  }

  if (type === 'custom' && name) {
    return h(CustomIcon, {
      icon: name as CustomIconName,
      size: size || '16px',
      style: { color },
      class: className,
    });
  }

  return null;
};

// Template ref for scrollable path
const scrollablePathRef = ref<HTMLElement | null>(null);

// Don't automatically inherit attributes - we're passing them through manually
defineOptions({
  inheritAttrs: false,
});

/**
 * ItemRow Configuration Interface
 * Defines how to render any type of item in a selector row
 */
export interface ItemRowConfig {
  // Icon configuration
  icon?: {
    type: 'avatar' | 'dot' | 'icon' | 'fluent' | 'custom';
    // For avatar
    user?: OrganizationMembership;
    hideInactiveWarning?: boolean;
    // For icon/fluent/custom
    name?: string;
    // For dot or icon color
    color?: string;
    size?: string;
    className?: string;
  };

  // Primary text configuration
  primary?: {
    text: string;
    color?: string;
    className?: string;
    indentLevel?: number; // For hierarchical indentation (e.g., WorkTypes)
  };

  // Secondary text configuration
  secondary?: {
    text?: string;
    className?: string;
    // For multi-part secondary text (like email + team)
    parts?: Array<{
      text: string;
      separator?: string;
      className?: string;
    }>;
    // For scrollable breadcrumb-style paths (auto-scrolls to show end)
    scrollablePath?: {
      items: string[];
      separator?: string;
    };
  };

  // Badge configuration
  badge?: {
    text?: string;
    color?: string;
    textColor?: string;
    outline?: boolean;
    // For multiple badges
    multiple?: boolean;
    items?: Array<{
      text: string;
      color?: string;
      textColor?: string;
      outline?: boolean;
    }>;
    showNavigationArrow?: boolean;
  };

  // Row behavior
  hierarchical?: boolean;
  hideCheckbox?: boolean;
  navigateOnClick?: boolean;
  showNavigationArrow?: boolean;
}

type ItemWithId = { id: string; [key: string]: unknown } | string | number;

interface Props {
  item: ItemWithId;
  config: ItemRowConfig;
  selected?: boolean;
  focused?: boolean;
  disabled?: boolean;
  archived?: boolean;
  showCheckbox?: boolean;
  customClass?: string | string[];
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  focused: false,
  disabled: false,
  archived: false,
  showCheckbox: false,
});

const emit = defineEmits<{
  click: [item: ItemWithId];
  toggle: [item: ItemWithId];
  select: [item: ItemWithId];
  navigate: [item: ItemWithId];
  'item-selected': [item: ItemWithId];
  'item-toggled': [item: ItemWithId];
  'navigate-into': [itemId: string];
}>();

// Computed classes for the two-row layout
const computedRowClasses = computed(() => {
  const classes: string[] = [];
  if (props.selected) classes.push('item-row--selected');
  if (props.focused) classes.push('item-row--focused');
  if (props.disabled) classes.push('item-row--disabled');
  if (props.archived) classes.push('item-row--archived');
  return classes;
});

// Handle click on the two-row layout
const handleRowClick = () => {
  if (props.disabled) return;

  if (props.config.navigateOnClick) {
    emit('navigate', props.item);
    emit('navigate-into', String(getItemId(props.item)));
  } else if (props.showCheckbox) {
    emit('item-toggled', props.item);
  } else {
    emit('item-selected', props.item);
  }
  emit('click', props.item);
};

// Auto-scroll scrollable path to the right (shows most relevant parent folder)
// Use onMounted for initial scroll to avoid performance issues in long lists
onMounted(() => {
  if (props.config.secondary?.scrollablePath && scrollablePathRef.value) {
    scrollablePathRef.value.scrollLeft = scrollablePathRef.value.scrollWidth;
  }
});

// Watch for subsequent changes (rare, but possible if props update after mount)
watch(
  () => props.config.secondary?.scrollablePath,
  async () => {
    if (props.config.secondary?.scrollablePath) {
      await nextTick();
      if (scrollablePathRef.value) {
        scrollablePathRef.value.scrollLeft =
          scrollablePathRef.value.scrollWidth;
      }
    }
  }
);

// Get full path as string for tooltip display
function getFullPathString(): string {
  const scrollablePath = props.config.secondary?.scrollablePath;
  if (!scrollablePath) return '';

  const separator = scrollablePath.separator || '›';
  const pathParts = [...scrollablePath.items];

  // Add the primary text (board/item name) at the end
  if (props.config.primary?.text) {
    pathParts.push(props.config.primary.text);
  }

  return pathParts.join(` ${separator} `);
}

// Compute primary text style including indentation
const getPrimaryStyle = computed(() => {
  const styles: Record<string, string> = {};

  if (props.config.primary?.color) {
    styles.color = props.config.primary.color;
  }

  if (props.config.primary?.indentLevel) {
    styles.paddingLeft = `${props.config.primary.indentLevel * 16}px`;
  }

  return styles;
});

const getItemId = (item: ItemWithId): string | number => {
  if (typeof item === 'string' || typeof item === 'number') {
    return item;
  }
  return item.id;
};

const handleClick = () => {
  if (props.config.navigateOnClick) {
    emit('navigate', props.item);
    emit('navigate-into', String(getItemId(props.item)));
  } else {
    // For backwards compatibility, still emit click
    emit('click', props.item);
  }
};

const handleItemSelected = (item: ItemWithId) => {
  // Forward the item-selected event from BaseRow
  emit('item-selected', item);
};

const handleItemToggled = (item: ItemWithId) => {
  // Forward the item-toggled event from BaseRow
  emit('item-toggled', item);
};

const handleNavigateClick = () => {
  // Emit navigate-into event when chevron is clicked
  emit('navigate-into', String(getItemId(props.item)));
};

// Helper to get badge classes with proper theme support
const getBadgeClasses = (
  badge: NonNullable<ItemRowConfig['badge']>
): string => {
  const classes: string[] = [];

  // Use the badge color for text (full opacity)
  // Background will be handled via inline styles with opacity
  if (badge.color) {
    classes.push(`tw-text-${badge.color}`);
  } else if (badge.textColor) {
    // Fallback to textColor if provided for backwards compatibility
    classes.push(`tw-text-${badge.textColor}`);
  }

  // Handle outline
  if (badge.outline) {
    classes.push('tw-border tw-border-current');
  }

  return classes.join(' ');
};

// Helper to get badge inline styles for background with opacity
const getBadgeStyles = (
  badge: NonNullable<ItemRowConfig['badge']>
): Record<string, string> => {
  const styles: Record<string, string> = {};

  if (badge.color) {
    // Apply the same color as background with 20% opacity
    // Using Tailwind's approach with RGB values
    const colorMap: Record<string, string> = {
      green: 'rgb(34 197 94 / 0.2)', // tw-bg-green with 20% opacity
      blue: 'rgb(59 130 246 / 0.2)', // tw-bg-blue with 20% opacity
      red: 'rgb(239 68 68 / 0.2)', // tw-bg-red with 20% opacity
      yellow: 'rgb(234 179 8 / 0.2)', // tw-bg-yellow with 20% opacity
      'gray-light': 'rgb(243 244 246 / 0.2)', // tw-bg-gray-light with 20% opacity
      'gray-medium': 'rgb(156 163 175 / 0.2)', // tw-bg-gray-medium with 20% opacity
      'gray-medium-dark': 'rgb(107 114 128 / 0.2)', // tw-bg-gray-medium-dark with 20% opacity
      'gray-dark': 'rgb(75 85 99 / 0.2)', // tw-bg-gray-dark with 20% opacity
      white: 'rgb(255 255 255 / 0.2)', // white with 20% opacity
    };

    styles.backgroundColor = colorMap[badge.color] || 'rgb(243 244 246 / 0.2)'; // Default to gray-light
  }

  return styles;
};
</script>

<style scoped lang="scss">
// Two-row layout styles
.item-row {
  @apply tw-cursor-pointer tw-border-b tw-border-gray-light tw-p-3;
  transition: background-color 0.15s ease;
  user-select: none;
  position: relative;
  z-index: 0;

  &:hover:not(.item-row--selected) {
    @apply tw-bg-black tw-bg-opacity-5;
  }

  &:focus-visible {
    @apply tw-outline-none;
    box-shadow: inset 0 0 0 1px var(--primary-color);
    z-index: 1;
  }

  &--selected {
    background-color: rgba(var(--primary-color-rgb), 0.2);

    &:hover {
      background-color: rgba(var(--primary-color-rgb), 0.25);
    }

    &:focus-visible {
      background-color: rgba(var(--primary-color-rgb), 0.25);
      box-shadow: inset 0 0 0 1px var(--primary-color);
    }
  }

  &--focused {
    box-shadow: inset 0 0 0 1px var(--primary-color);
    z-index: 1;
  }

  &--disabled {
    @apply tw-opacity-50 tw-cursor-not-allowed;
    pointer-events: none;
  }

  &--archived {
    @apply tw-opacity-60;
  }
}

.navigation-chevron {
  transition: background-color 0.15s ease;

  &:hover {
    background-color: color-mix(in srgb, var(--primary-color) 30%, transparent);
  }
}

// Scrollable breadcrumb path for search results
.scrollable-path {
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }

  // Alignment variants for two-row layout
  &--with-checkbox {
    margin-left: 44px;
  }

  &--no-checkbox {
    margin-left: 24px;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    font-size: 0.75rem;
    line-height: 1rem;
  }

  &__separator {
    flex-shrink: 0;
  }

  &__item {
    flex-shrink: 0;
  }
}
</style>
